<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import WaveSurfer from 'wavesurfer.js';
	import pRetry from 'p-retry';
	import _ from 'lodash';
	import { WaveSurferPauseFunctions } from './blackboard';
	import { Button } from './shadcn/components/ui/button';
	import { Slider } from './shadcn/components/ui/slider';
	import { Progress } from './shadcn/components/ui/progress';
	import { Pause, Play, Download } from '@lucide/svelte';
	import { cn } from './shadcn/utils';
	import { fetchRetry, retry } from './utils';

	let element = $state<HTMLDivElement>();
	let wavesurfer = $state<WaveSurfer>();
	let volume = $state(0.5);
	let loaded = $state(false);
	let playing = $state(false);
	let progress = $state<number | null>(null);

	let {
		url,
		peaksUrl,
		name = '',
		downloadEnabled = false,
		downloadDisabledMessage = '',
		downloadDisabledFn = () => {},
		downloadHidden = false,
		variant = 'default',
		class: className
	}: {
		url: string;
		peaksUrl?: string;
		name?: string;
		downloadEnabled?: boolean;
		downloadDisabledMessage?: string;
		downloadDisabledFn?: () => void;
		downloadHidden?: boolean;
		variant?: 'default' | 'ghost';
		class?: string;
	} = $props();

	let downloadUrl = $state<string>();

	const waveColor = $derived.by(() => {
		switch (variant) {
			case 'default':
				return '#111111';
			case 'ghost':
				return '#eb4999';
		}
	});

	const progressColor = $derived.by(() => {
		switch (variant) {
			case 'default':
				return '#06b6d480';
			case 'ghost':
				return '#eb499980';
		}
	});

	const cursorColor = $derived.by(() => {
		switch (variant) {
			case 'default':
				return '#06b6d4';
			case 'ghost':
				return '#eb4999';
		}
	});

	async function loadWithPeaks() {
		if (element === undefined) {
			return;
		}

		const req = await pRetry(() => fetch(peaksUrl!));
		const peaks = (await req.json()).data;

		wavesurfer = WaveSurfer.create({
			container: element,
			height: 'auto',
			waveColor,
			cursorColor,
			progressColor,
			barWidth: 2,
			barRadius: 2,
			barGap: 2,
			normalize: true,
			url: url,
			peaks: peaks
		});

		downloadUrl = url;
	}
	async function loadWithoutPeaks() {
		if (element === undefined) {
			return;
		}

		wavesurfer = WaveSurfer.create({
			container: element,
			height: 'auto',
			waveColor,
			cursorColor,
			progressColor,
			backend: 'WebAudio',
			barWidth: 2,
			barRadius: 2,
			barGap: 2,
			normalize: true
		});

		const fetchProgressImport = await import('fetch-progress');
		const fetchProgress = fetchProgressImport.default;

		const fetchInterceptor = fetchProgress({
			onProgress: _.throttle((e) => {
				if (e.total === 0) {
					progress = null;
				}
				progress = Math.round((e.transferred / e.total) * 100);
			}, 1000)
		});

		const downloadUrlResponse = await fetchRetry(url);
		const downloadUrlObj = await downloadUrlResponse.json();

		const response = await retry(async () =>
			fetchInterceptor(await fetch(downloadUrlObj.url, { mode: 'cors' }))
		);
		const blob = await response.blob();

		downloadUrl = URL.createObjectURL(blob);

		wavesurfer.loadBlob(blob);
	}

	onMount(async () => {
		downloadUrl = url;

		if (peaksUrl) {
			await loadWithPeaks();
		} else {
			await loadWithoutPeaks();
		}

		WaveSurferPauseFunctions.set(wavesurfer!, pressPause);

		wavesurfer!.on('interaction', () => {
			pressPlay();
		});
		wavesurfer!.setVolume(volume);

		loaded = true;
	});

	onDestroy(() => {
		if (undefined === wavesurfer) {
			return;
		}

		WaveSurferPauseFunctions.delete(wavesurfer);
	});

	$effect(() => {
		wavesurfer?.setVolume(volume);
	});

	function pressPlay() {
		WaveSurferPauseFunctions.entries()
			.filter((x) => x[0] !== wavesurfer!)
			.forEach((x) => x[1]());

		wavesurfer?.play();
		playing = true;
	}
	function pressPause() {
		wavesurfer?.pause();
		playing = false;
	}
</script>

<div class={cn('grid h-28 grid-cols-1 grid-rows-1 overflow-hidden rounded-xl', className)}>
	<div
		class={cn('col-start-1 row-start-1 rounded-xl p-2', {
			'bg-primary': variant == 'default',
			'preset-tonal': variant == 'ghost',
			border: variant == 'ghost',
			'border-primary/50': variant == 'ghost'
		})}
		class:hidden={!loaded}
	>
		<div class="h-full" bind:this={element}></div>
	</div>
	{#if loaded}
		<div
			class="pointer-events-none z-10 col-start-1 row-start-1 flex items-center gap-2 self-start p-2"
		>
			<p class="{cn({ 'bg-background/80 rounded-sm px-2': variant == 'ghost' })})">{name}</p>
		</div>
		<div
			class="pointer-events-none z-10 col-start-1 row-start-1 flex items-center gap-2 self-end p-2"
		>
			{#if playing}
				<Button
					type="button"
					variant={variant == 'default' ? 'filled' : 'ghost'}
					size="icon-sm"
					class="pointer-events-auto"
					aria-label="pause"
					onclick={pressPause}><Pause /></Button
				>
			{:else}
				<Button
					type="button"
					variant={variant == 'default' ? 'filled' : 'ghost'}
					size="icon-sm"
					class="pointer-events-auto"
					onclick={pressPlay}
					aria-label="pause"><Play /></Button
				>
			{/if}
			<Slider
				bind:value={volume}
				type="single"
				min={0.0}
				max={1.0}
				step={0.001}
				class="**:data-[slot='slider-range']:bg-muted-foreground **:data-[slot='slider-thumb']:bg-muted **:data-[slot='slider-thumb']:border-muted-foreground pointer-events-auto w-20 max-w-20 **:data-[slot='slider-track']:h-1"
				aria-label="change volume"
			/>
			{#if !downloadHidden}
				{#if downloadEnabled}
					<a href={downloadUrl} download={name}>
						<Button
							variant="filled"
							size="icon-sm"
							class="pointer-events-auto"
							aria-label="download"
						>
							<Download />
						</Button>
					</a>
				{:else}
					<Button
						variant="filled"
						size="icon-sm"
						class="pointer-events-auto"
						onclick={downloadDisabledFn}><Download /> {downloadDisabledMessage}</Button
					>
				{/if}
			{/if}
		</div>
	{:else}
		<div
			class="preset-tonal animate-shimmer flex animate-pulse items-center justify-center rounded-xl"
		>
			<div class="flex h-20 w-full flex-col items-center justify-center p-4">
				{#if progress}
					<Progress value={progress} />
				{/if}
			</div>
		</div>
	{/if}
</div>
